<!-- Responsibility: Manage the entitlement catalog and commerce plans.
Scope: List and form for commerce plans. -->
<admin-commerce-card>
  <section class="admin-card">
    <header>
      <h2>Entitlement Catalog</h2>
      <button type="button" class="action-btn subtle" onclick={ () => openModal() }>ADD PLAN</button>
    </header>
    <p class="admin-copy">Phase 1 commerce control: define the managed plans that billing, proxy access, marketplace gating, and preforged realities will align to.</p>
    <p if={ props.commerceMessage } class="admin-note">{ props.commerceMessage }</p>
    <ul class="admin-roster">
      <li each={ plan in props.commercePlans }>
        <div class="admin-roster-copy">
          <strong>{ plan.label }</strong>
          <span>{ plan.id } · ${ ((plan.monthlyPriceUsdCents || 0) / 100).toFixed(2) } / month · { plan.active ? 'Active' : 'Hidden' }</span>
        </div>
        <div class="admin-roster-meta">
          <small>{ plan.managedActionQuota || 0 } managed actions · { plan.managedImageQuota || 0 } managed images · { plan.creatorPublishSlots || 0 } creator slots</small>
          <small>{ plan.hostedProxyAccess ? 'Hosted proxy' : 'No hosted proxy' } · { plan.preforgedRealityAccess ? 'Preforged realities included' : 'No preforged realities' }</small>
        </div>
        <div class="admin-actions compact">
          <button type="button" class="action-btn subtle" onclick={ () => editCommercePlan(plan) }>EDIT</button>
        </div>
      </li>
    </ul>

    <dialog ref="commerceModal" class="admin-form-modal">
      <div class="admin-modal-content">
        <header>
          <h3>Manage Plan</h3>
          <button type="button" class="modal-close-hit" onclick={ () => closeModal() }>✕</button>
        </header>
        <div ref="commerceHost" class="admin-form-host"></div>
        <div class="admin-actions">
          <button type="button" class="action-btn primary" onclick={ onSavePlan }>SAVE PLAN</button>
          <button type="button" class="action-btn" onclick={ onResetForm }>RESET</button>
        </div>
      </div>
    </dialog>
  </section>

  <script>
    import { AutoForm, collectValues, populateFields } from 'field-assist'
    import { buildAdminCommerceFormMeta, buildAdminCommerceInitialValues, buildAdminCommerceSubmission } from '../../helpers/admin-commerce-form'

    export default {
      state: {
        currentPlan: null
      },

      onMounted() {
        // Form is rendered when modal opens
      },

      openModal(plan = null) {
        this.state.currentPlan = plan
        const dialog = this.$('[ref="commerceModal"]')
        if (dialog && typeof dialog.showModal === 'function') {
          dialog.showModal()
          this.renderForm(plan)
        }
      },

      closeModal() {
        const dialog = this.$('[ref="commerceModal"]')
        if (dialog && typeof dialog.close === 'function') {
          dialog.close()
        }
      },

      renderForm(plan = null) {
        const host = this.$('[ref="commerceHost"]')
        if (!(host instanceof HTMLElement)) return
        host.replaceChildren()
        
        const initialValues = buildAdminCommerceInitialValues(plan || {})
        const autoForm = new AutoForm(buildAdminCommerceFormMeta())
        autoForm.render(initialValues, host)
        populateFields(host, initialValues, 'name')
      },

      editCommercePlan(plan) {
        this.openModal(plan)
      },

      onSavePlan() {
        const host = this.$('[ref="commerceHost"]')
        if (!(host instanceof HTMLElement)) return
        const values = collectValues(host, undefined, 'name')
        const plan = buildAdminCommerceSubmission(values)
        
        if (this.props.onSavePlan) {
          this.props.onSavePlan(plan)
        }
        this.closeModal()
      },

      onResetForm() {
        this.renderForm(this.state.currentPlan)
      }
    }
  </script>

  <style>
    .admin-card {
      display: grid;
      gap: var(--space-sm);
      border: 1px solid color-mix(in srgb, var(--border) 46%, transparent);
      border-radius: 1rem;
      padding: var(--space-md);
      background:
        linear-gradient(180deg, color-mix(in srgb, var(--surface-raised) 88%, transparent), color-mix(in srgb, var(--bg) 88%, transparent)),
        radial-gradient(circle at top right, color-mix(in srgb, var(--gold) 14%, transparent), transparent 58%);
      box-shadow: var(--shadow-elev-2);
    }

    header {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: var(--space-sm);
    }

    h2 {
      margin: 0;
      color: var(--text-prose);
    }

    .admin-copy,
    .admin-note {
      color: var(--text-muted);
      font-size: var(--type-sm, 0.875rem);
      line-height: 1.6;
      margin: 0;
    }

    .admin-roster {
      display: grid;
      gap: var(--space-xs);
      margin: 0;
      padding: 0;
      list-style: none;
    }

    .admin-roster li {
      display: grid;
      gap: var(--space-xs);
      padding: var(--space-sm);
      border-radius: 0.9rem;
      border: 1px solid color-mix(in srgb, var(--border) 28%, transparent);
      background: color-mix(in srgb, var(--surface-raised) 82%, transparent);
    }

    .admin-roster-copy,
    .admin-roster-meta {
      display: grid;
      gap: 0.16rem;
    }

    .admin-roster-copy strong {
      color: var(--text-prose);
    }

    .admin-roster-copy span,
    .admin-roster-meta small {
      color: var(--text-muted);
      font-size: var(--type-xs, 0.78rem);
      line-height: 1.45;
    }

    .admin-actions {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: var(--space-xs);
    }

    .admin-actions.compact {
      justify-content: flex-start;
    }

    .admin-form-modal {
      background: transparent;
      border: none;
      padding: 0;
      margin: auto;
      max-width: 56rem;
      width: min(94vw, 56rem);
      max-height: 88vh;
      overflow: hidden;
    }
    
    .admin-form-modal::backdrop {
      background: color-mix(in srgb, var(--surface) 90%, black);
      backdrop-filter: blur(8px);
    }

    .admin-modal-content {
      background: var(--surface-card-1);
      border: 1px solid var(--border);
      border-radius: 1rem;
      padding: var(--space-md);
      display: grid;
      gap: var(--space-md);
      box-shadow: var(--shadow-elev-4);
      max-height: 88vh;
      overflow: auto;
    }

    .admin-modal-content header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: var(--space-sm);
      border-bottom: 1px solid var(--border);
    }
    
    .admin-modal-content h3 {
      margin: 0;
      color: var(--text-prose);
      font-size: var(--type-md);
    }

    .admin-form-host {
      display: grid;
      gap: var(--space-sm);
    }
  </style>
</admin-commerce-card>
